import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { Card, CardContent } from "@/components/ui/card";
import { Trophy } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import PlayerLeagueCard from "@/components/leagues/PlayerLeagueCard";

// PR-16.2: shape now includes the new sponsor URL columns from PR-16.1
// so the shared <PlayerLeagueCard> can render the variant-specific
// logo + click-through link.
type LeagueWithHall = Tables<"leagues"> & {
  halls: { name: string } | null;
};

interface LeagueListProps {
  status: string;
  userId?: string;
}

export default function LeagueList({ status, userId: _userId }: LeagueListProps) {
  const { data: leagues, isLoading } = useQuery({
    queryKey: ["leagues", status],
    queryFn: async () => {
      // PR-16.2: explicit column list now includes the sponsor URL
      // fields. We still use `*` for backward-compat with any consumers
      // that read other columns (cost_per_player, members_play_free,
      // etc.); the spread plus halls(name) covers the new card needs.
      const { data, error } = await supabase
        .from("leagues")
        .select("*, halls(name)")
        .eq("status", status)
        .order("start_date", { ascending: status === "registration_open" });
      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => <Skeleton key={i} className="h-28 w-full rounded-lg" />)}
      </div>
    );
  }

  if (!leagues || leagues.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          <Trophy className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>{status === "registration_open" ? "Inga öppna serier just nu" : "Inga avslutade serier"}</p>
        </CardContent>
      </Card>
    );
  }

  // PR-16.2: variant per spec — Anmäl dig (registration_open) shows the
  // register-variant sponsor + registration_deadline; Arkiv (completed)
  // dims the same card without sponsor + uses register-variant fallback
  // text where needed. Other statuses (active/draft/archived) shown via
  // LeagueListPanel desktop sidebar — not via this list.
  const variant = status === "registration_open" ? "register" : "archive";

  return (
    <div className="space-y-3">
      {leagues.map((league: LeagueWithHall) => (
        <PlayerLeagueCard
          key={league.id}
          league={league}
          variant={variant}
        />
      ))}
    </div>
  );
}
